import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    applyEdgeChanges,
    applyNodeChanges,
    BaseEdge,
    EdgeLabelRenderer,
    EdgeProps,
    getBezierPath,
    useReactFlow,
} from '@xyflow/react'
import { ClockCircleOutlined, ShoppingCartOutlined, SnippetsFilled, WifiOutlined, SaveOutlined } from '@ant-design/icons'
import './custom-edge.scss'
import { getClassNames } from '../../../../_utils/classnames'
import { Button } from 'antd'
import { getAddNodeMutations } from '../../helpers'
import { useJourneyContext } from '../../context'
import { JourneyBuilderMode } from '../../enums'
import { ActionStep, AddStepType, DelayStep, JourneyEdge, JourneyNode } from '../../types/journey-nodes'
import { EdgeAction } from './edge-action'
import { InvalidJourneyComponent } from './invalid-journey-component'
import { CopyStates, CopyTypes } from '../../types/journey-context'
import { JourneyActionType } from '@pushly/models/lib/enums/journeys/journey-action-type'
import { RECOMMENDATION_NOTIFICATION_INTEGRATIONS } from '../../constants'
import { titleCase } from 'change-case'
import { JourneyAction } from '../../types/journey-context'
import { StepType, TriggerType } from '@pushly/models/lib/enums/journeys'
import { getIconByActionType } from '../../node-types/custom-node/helpers'
import { safeEnumFromValue } from '@pushly/models'

export const CustomEdge = ({
    id,
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    source,
    target,
    data,
    ...props
}: EdgeProps<JourneyEdge>) => {
    // region Custom Edge Definitions

    const [state, dispatch] = useJourneyContext()
    const { getNode, getNodes, setNodes, setEdges } = useReactFlow<JourneyNode>()

    const [edgeActionOptions, setEdgeActionOptions] = useState<AddStepType[]>([StepType.DELAY])
    const [showEdgeActions, setShowEdgeActions] = useState(false)
    const [showInvalidConnection, setShowInvalidConnection] = useState(false)

    const [edgePath, labelX, labelY] = getBezierPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
    })

    const buildEdgeActionNodeButtons = useMemo(() => {
        // state.copy.state === CopyStates.FILLED, state.copy.node
        const actionNodes: React.ReactNode[] = []

        if (state.copy.state === CopyStates.FILLED && state.copy.node !== null) {
            const node = state.copy.node
            actionNodes.push(
                <Button
                    size="small"
                    type="primary"
                    shape="circle"
                    className="journey-edge-action-paste"
                    onClick={() => {
                        if (!node) return

                        const stepToPaste = node.data.step
                        let nextStepType

                        if (stepToPaste.type === StepType.ACTION) {
                            nextStepType = node.data.step.configuration.type
                        } else if (stepToPaste.type === StepType.DELAY) {
                            nextStepType = StepType.DELAY
                        } else {
                            return
                        }

                        handleAddNode(nextStepType, {
                            override: node.data.step,
                            overrideType: state.copy.type,
                            skipOpenEditor: true,
                        })
                        dispatch({ action: JourneyAction.SET_COPY_STATE, data: { node: null } })
                    }}
                >
                    <SnippetsFilled />
                </Button>,
            )
        }

        edgeActionOptions.forEach((addStepType) => {
            switch (addStepType) {
                case JourneyActionType.SEND_CART_NOTIFICATION:
                    actionNodes.push(
                        <Button
                            size="small"
                            type="primary"
                            shape="circle"
                            className="journey-edge-action-send_cart_notification"
                            onClick={() => handleAddNode(JourneyActionType.SEND_CART_NOTIFICATION)}
                        >
                            <ShoppingCartOutlined />
                        </Button>,
                    )
                    break
                case JourneyActionType.SEND_SAVED_ITEM_NOTIFICATION:
                    actionNodes.push(
                        <Button
                            size="small"
                            type="primary"
                            shape="circle"
                            className="journey-edge-action-send_saved_item_notification"
                            onClick={() => handleAddNode(JourneyActionType.SEND_SAVED_ITEM_NOTIFICATION)}
                        >
                            <SaveOutlined />
                        </Button>,
                    )
                    break
                case JourneyActionType.SEND_NOTIFICATION:
                    actionNodes.push(
                        <Button
                            size="small"
                            type="primary"
                            shape="circle"
                            className="journey-edge-action-send_notification"
                            onClick={() => handleAddNode(JourneyActionType.SEND_NOTIFICATION)}
                        >
                            <WifiOutlined />
                        </Button>,
                    )
                    break
                case JourneyActionType.SEND_RECOMMENDED_NOTIFICATION:
                    actionNodes.push(
                        <Button
                            size="small"
                            type="primary"
                            shape="circle"
                            className="journey-edge-action-send_recommended_notification"
                            onClick={() => handleAddNode(JourneyActionType.SEND_RECOMMENDED_NOTIFICATION)}
                        >
                            {getIconByActionType(JourneyActionType.SEND_RECOMMENDED_NOTIFICATION)}
                        </Button>,
                    )
                    break
                case StepType.DELAY:
                    actionNodes.push(
                        <Button
                            size="small"
                            type="primary"
                            shape="circle"
                            className="journey-edge-action-delay"
                            onClick={() => handleAddNode(StepType.DELAY)}
                        >
                            <ClockCircleOutlined />
                        </Button>,
                    )
                    break
                default:
                    break
            }
        })

        return actionNodes
    }, [state.nodes, state.edges, state.copy])

    // endregion
    // region Interaction Handlers

    const handleAddNode = useCallback(
        (
            stepType: AddStepType,
            addConfig: { override?: ActionStep | DelayStep; overrideType?: CopyTypes; skipOpenEditor?: boolean } = {},
        ) => {
            const sourceNode = getNode(source)
            const targetNode = getNode(target)

            if (sourceNode && targetNode) {
                const [nodeChanges, edgeChanges, newNode] = getAddNodeMutations(
                    stepType,
                    getNodes(),
                    getNode(source)!,
                    getNode(target)!,
                    addConfig.override,
                    addConfig.overrideType,
                )

                setNodes((nodes) => applyNodeChanges(nodeChanges, nodes))
                setEdges((edges) => applyEdgeChanges(edgeChanges, edges))

                if (!addConfig.skipOpenEditor) {
                    dispatch({ action: JourneyAction.ON_NODE_ADD, data: newNode! })
                }
            }
        },
        [source, target],
    )

    // endregion

    // region onComponentMount effect
    useEffect(() => {
        const getAvailableActionNodeOptions = () => {
            const currOptions = Array.from(edgeActionOptions)

            const triggerType: TriggerType = safeEnumFromValue(
                TriggerType,
                state.journey?.configuration?.computed_trigger_type ?? TriggerType.SUBSCRIBED,
            )

            // we always want to allow them to send a custom notification
            currOptions.push(JourneyActionType.SEND_NOTIFICATION)

            // currently Abandoned Cart/Browse only supported ecomm campaign triggers
            const ecommTriggers = [TriggerType.ABANDONED_CART, TriggerType.ABANDONED_VIEW]
            if (ecommTriggers.includes(triggerType)) {
                currOptions.push(JourneyActionType.SEND_CART_NOTIFICATION)
            }

            const domainCanCreateRecommendedNotifications = Array.from(
                state.dependencies.integrations?.entries() ?? [],
            )?.some(([_id, int]) => RECOMMENDATION_NOTIFICATION_INTEGRATIONS.includes(int.provider) && int.is_active)

            if (domainCanCreateRecommendedNotifications) {
                currOptions.push(JourneyActionType.SEND_RECOMMENDED_NOTIFICATION)
            }

            const savedItemTriggers = [TriggerType.SAVED_ITEM]
            if (savedItemTriggers.includes(triggerType)) {
                currOptions.push(JourneyActionType.SEND_SAVED_ITEM_NOTIFICATION)
            }

            setEdgeActionOptions(currOptions)
        }

        getAvailableActionNodeOptions()
    }, [state.journey?.id])

    // watch for invalid connections and show UI warning when node->node incompatibility
    useEffect(() => {
        const sourceNode = getNode(source)
        const targetNode = getNode(target)
        if (sourceNode && targetNode) {
            const sourceType = sourceNode.data.step.type
            const targetType = targetNode.data.step.type

            const invalidDelayConnections = sourceType === StepType.DELAY && targetType === StepType.DELAY
            const invalidActionConnections = sourceType === StepType.ACTION && targetType === StepType.ACTION
            const invalidStepType: StepType | null = invalidDelayConnections
                ? StepType.DELAY
                : invalidActionConnections
                ? StepType.ACTION
                : null

            if (invalidStepType) {
                setShowInvalidConnection(true)
            } else {
                if (showInvalidConnection) {
                    setShowInvalidConnection(false)
                }
            }
        }
    }, [source, target])

    // endregion

    const invalidConnectionMessage = useMemo(() => {
        const sourceType = getNodes().find((n) => n.id === source)?.data?.step?.type

        return sourceType
            ? `${titleCase(
                  sourceType,
              )} Steps cannot be adjacent. Please remove one of the ${sourceType?.toLowerCase()}s or add a step between them.`
            : 'Invalid connection. Please remove one of the steps or add a step between them.'
    }, [source])

    return (
        <>
            <BaseEdge id={id} path={edgePath} markerEnd={props.markerEnd} markerStart={props.markerStart} />
            <EdgeLabelRenderer>
                {showInvalidConnection && (
                    <div
                        className="journey-invalid-label"
                        style={{
                            transform: `translate(-225%, -50%) translate(${labelX + 20}px, ${labelY}px)`,
                        }}
                    >
                        <InvalidJourneyComponent content={invalidConnectionMessage} />
                    </div>
                )}
                <div
                    className={getClassNames(null, 'journey-edge-custom', {
                        open: showEdgeActions,
                        interactive: state.mode !== JourneyBuilderMode.READONLY,
                    })}
                    style={{
                        transform: `translate(-50%, -50%) translate(${labelX + 20}px, ${labelY}px)`,
                    }}
                >
                    <div
                        className={getClassNames('journey-edge-actions', {
                            open: showEdgeActions,
                        })}
                    >
                        <EdgeAction visible={showEdgeActions} onClick={() => setShowEdgeActions(!showEdgeActions)} />
                        <div className="journey-edge-action-row">
                            <div className="journey-edge-action-options">{...buildEdgeActionNodeButtons}</div>
                        </div>
                    </div>
                </div>
            </EdgeLabelRenderer>
        </>
    )
}
